import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  getPersonaImageUrl,
  getPublicPersonaImageUrl,
} from '../../services/apiService';
import { useUserStatus } from '../../context/UserStatusContext';
import styles from './PersonaImage.module.css';

function PersonaImage({
  personaId,
  alt,
  className,
  isOwner,
  username,
  cacheKey,
}) {
  const { imageUrlCache } = useUserStatus();
  const CACHE_LIMIT = 100;

  const [imageUrl, setImageUrl] = useState(null);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    const fetchImageUrl = async () => {
      if (!personaId && !username) return;

      const cacheId = `${personaId}-${cacheKey}`;

      if (imageUrlCache.has(cacheId)) {
        setImageUrl(imageUrlCache.get(cacheId));
        return;
      }

      try {
        let data;

        if (isOwner) {
          const accessToken = await getAccessTokenSilently();
          data = await getPersonaImageUrl(personaId, accessToken, cacheKey);
        } else {
          data = await getPublicPersonaImageUrl(username, cacheKey);
        }

        if (data && data.url) {
          imageUrlCache.set(cacheId, data.url);
          if (imageUrlCache.size > CACHE_LIMIT) {
            const oldestKey = imageUrlCache.keys().next().value;
            imageUrlCache.delete(oldestKey);
          }
          setImageUrl(data.url);
        }
      } catch (error) {
        if (error.message && error.message.includes('not found')) {
          console.log(
            `PersonaImage: No image for ${personaId || username}. Using default.`
          );
        } else {
          console.error('Failed to fetch image URL', error);
        }
      }
    };

    setIsImageLoaded(false);
    setImageUrl(null);
    fetchImageUrl();
  }, [
    personaId,
    isOwner,
    username,
    cacheKey,
    getAccessTokenSilently,
    imageUrlCache,
  ]);

  return (
    <div className={`${styles.imageContainer} ${className}`}>
      {!isImageLoaded && <div className={styles.placeholder}></div>}

      <img
        src={imageUrl || '/default-avatar.png'}
        alt={alt}
        className={`${styles.image} ${isImageLoaded ? styles.visible : ''}`}
        onLoad={() => setIsImageLoaded(true)}
        crossOrigin="anonymous"
      />
    </div>
  );
}

export default PersonaImage;
